import { create } from 'zustand';
import type { Inventory, Customer, InvoiceDiscount } from '@motra/shared';

export interface BasketItem {
  item: Inventory;
  qty: number;
  discount?: {
    type: 'percentage' | 'fixed';
    val: number;
  };
}

export interface PaymentInfo {
  method: string;
  amount: number;
  received: number;
  change: number;
  chargeId?: string;
  signature?: string;
}

interface PosState {
  basket: BasketItem[];
  customer: Customer | null;
  payment: PaymentInfo | null;
  discount: InvoiceDiscount | null;
  taxes: { name: string; value: number }[];

  // Basket actions
  addItem: (item: Inventory, qty?: number) => void;
  removeItem: (index: number) => void;
  adjustQty: (index: number, qty: number) => void;
  setItemDiscount: (index: number, discount: BasketItem['discount']) => void;

  // Customer
  setCustomer: (customer: Customer) => void;
  clearCustomer: () => void;

  // Discount
  setDiscount: (discount: InvoiceDiscount) => void;
  clearDiscount: () => void;

  // Taxes
  setTaxes: (taxes: { name: string; value: number }[]) => void;

  // Payment
  setPayment: (payment: PaymentInfo) => void;

  // Calculations
  getSubtotal: () => number;
  getTaxTotal: () => number;
  getTotal: () => number;
  getProfit: () => number;

  // Reset
  clearBasket: () => void;
}

function calcLineTotal(basketItem: BasketItem): number {
  const base = (basketItem.item.pprice ?? basketItem.item.price ?? 0) * basketItem.qty;
  if (!basketItem.discount) return base;
  if (basketItem.discount.type === 'percentage') {
    return base * (1 - basketItem.discount.val / 100);
  }
  return Math.max(0, base - basketItem.discount.val * basketItem.qty);
}

export const usePosStore = create<PosState>()((set, get) => ({
  basket: [],
  customer: null,
  payment: null,
  discount: null,
  taxes: [],

  addItem: (item, qty = 1) =>
    set((state) => {
      const existingIndex = state.basket.findIndex((b) => b.item.id === item.id);
      if (existingIndex >= 0) {
        const updated = [...state.basket];
        updated[existingIndex] = {
          ...updated[existingIndex],
          qty: updated[existingIndex].qty + qty,
        };
        return { basket: updated };
      }
      return { basket: [...state.basket, { item, qty }] };
    }),

  removeItem: (index) =>
    set((state) => ({
      basket: state.basket.filter((_, i) => i !== index),
    })),

  adjustQty: (index, qty) =>
    set((state) => {
      if (qty <= 0) {
        return { basket: state.basket.filter((_, i) => i !== index) };
      }
      const updated = [...state.basket];
      updated[index] = { ...updated[index], qty };
      return { basket: updated };
    }),

  setItemDiscount: (index, discount) =>
    set((state) => {
      const updated = [...state.basket];
      updated[index] = { ...updated[index], discount };
      return { basket: updated };
    }),

  setCustomer: (customer) => set({ customer }),
  clearCustomer: () => set({ customer: null }),

  setDiscount: (discount) => set({ discount }),
  clearDiscount: () => set({ discount: null }),

  setTaxes: (taxes) => set({ taxes }),

  setPayment: (payment) => set({ payment }),

  getSubtotal: () => {
    const { basket } = get();
    return basket.reduce((sum, b) => sum + calcLineTotal(b), 0);
  },

  getTaxTotal: () => {
    const { taxes } = get();
    const subtotal = get().getSubtotal();
    const discountedSubtotal = (() => {
      const { discount } = get();
      if (!discount) return subtotal;
      if (discount.type === 'percentage') return subtotal * (1 - discount.val / 100);
      return Math.max(0, subtotal - discount.val);
    })();
    return taxes.reduce((sum, t) => sum + discountedSubtotal * (t.value / 100), 0);
  },

  getTotal: () => {
    const subtotal = get().getSubtotal();
    const { discount } = get();
    let discountedSubtotal = subtotal;
    if (discount) {
      if (discount.type === 'percentage') {
        discountedSubtotal = subtotal * (1 - discount.val / 100);
      } else {
        discountedSubtotal = Math.max(0, subtotal - discount.val);
      }
    }
    return discountedSubtotal + get().getTaxTotal();
  },

  getProfit: () => {
    const { basket } = get();
    return basket.reduce((sum, b) => {
      const revenue = calcLineTotal(b);
      const cost = (b.item.cost ?? 0) * b.qty;
      return sum + (revenue - cost);
    }, 0);
  },

  clearBasket: () =>
    set({
      basket: [],
      customer: null,
      payment: null,
      discount: null,
    }),
}));
